/**
 * Auth API methods — thin typed wrappers around the gateway proxies.
 *
 * Every method returns a discriminated success/error union via
 * `apiJson` + `ApiError`. The store layer (zustand) consumes these
 * and surfaces error codes to react-i18next.
 *
 * Self-service `changePassword` lets a signed-in user rotate their OWN
 * password (verify current → set new) via POST /api/auth/change-password.
 * Admin-driven reset (Settings → Users) remains the path for rotating
 * OTHER users' passwords (founder-only).
 */
import type {
  AuthSessionResponse,
  LoginRequest,
  MeResponse,
  SignupRequest,
  User,
} from '@chaos/types/auth';

import { beginAuthTransition, getFreshVerifiedMe } from '../verified-principal';
import { apiJson, apiUpload } from './client';

export const authApi = {
  async signup(payload: SignupRequest): Promise<MeResponse> {
    const transition = beginAuthTransition('signup');
    try {
      await apiJson<AuthSessionResponse>('/auth/signup', { method: 'POST', json: payload });
      // A cookie-setting response is not private-data authority. Resolve with
      // only the fresh, no-store /me accepted under its principal fence.
      return await transition.getFreshVerifiedMe();
    } finally {
      // Also releases peers after a rejected or aborted POST or /me.
      transition.complete();
    }
  },

  async login(payload: LoginRequest): Promise<MeResponse> {
    const transition = beginAuthTransition('login');
    try {
      await apiJson<AuthSessionResponse>('/auth/login', { method: 'POST', json: payload });
      return await transition.getFreshVerifiedMe();
    } finally {
      transition.complete();
    }
  },

  async logout(): Promise<{ ok: boolean }> {
    // Revoke every tab before the cookie-mutating request starts. Peers do not
    // revalidate until completion, after server revocation (or failure/abort).
    const transition = beginAuthTransition('logout');
    try {
      return await apiJson<{ ok: boolean }>('/auth/logout', { method: 'POST' });
    } finally {
      transition.complete();
    }
  },

  changePassword(payload: {
    currentPassword: string;
    newPassword: string;
  }): Promise<{ ok: boolean }> {
    return apiJson<{ ok: boolean }>('/auth/change-password', { method: 'POST', json: payload });
  },

  me(): Promise<MeResponse> {
    return getFreshVerifiedMe();
  },

  // Self-service profile edit for the CURRENT user → PATCH /api/auth/me.
  // Send only the changed fields. The session stays valid (the JWT is keyed
  // on the user id, not the email). On failure `apiJson` throws an ApiError
  // the caller can introspect: 409 `email_taken`, 400 `invalid_email` /
  // `bad_request`. Returns the updated user so the store can re-seed.
  updateProfile(payload: { displayName?: string; email?: string }): Promise<User> {
    return apiJson<{ user: User }>('/auth/me', { method: 'PATCH', json: payload }).then(
      (r) => r.user,
    );
  },

  // ── Self-service account avatar (profile photo) ────────────────────
  // Mirrors orgsApi.uploadLogo: multipart FormData via apiFetch (Content-Type
  // left unset so the browser adds the boundary). Operates on the CURRENT
  // user only. This is the per-USER photo — NOT an org logo.
  //
  // WHAT THIS ROUTE NEEDED, and what it deliberately did not (2026-08-16).
  // Needed: the pre-send verified read `apiUpload` now performs — a picked
  // File goes stale if it is moved or deleted, and before this that reached
  // the transport as a "network error" and was retried three times. Also the
  // fail-fast on 413/415, which this route is the likeliest of all to hit: the
  // ceiling is 1 MB and the type list is png/jpeg/webp/gif/svg. NOT needed:
  // resumable chunks or a browser-side copy of the bytes. A 1 MB avatar is
  // still sitting on the uploader's disk; duplicating it into IndexedDB would
  // buy nothing and cost quota. Compare lib/voice-upload.ts, where the bytes
  // exist nowhere else and the whole apparatus is therefore justified.
  uploadAvatar(file: File): Promise<User> {
    return apiUpload<{ user: User }>('/auth/me/avatar', file, {
      filename: file.name,
    }).then((r) => r.user);
  },
  /** Remove the caller's account avatar (falls back to initials). */
  removeAvatar(): Promise<User> {
    return apiJson<{ user: User }>('/auth/me/avatar', { method: 'DELETE' }).then((r) => r.user);
  },
  // NB: there is intentionally no `refresh()` here. The gateway exposes
  // only /api/auth/{login,logout,me,change-password} — there is no
  // /auth/refresh route, so the old vestigial wrapper only ever 401'd
  // and was dead code. Session renewal happens by re-login. (Removed
  // 2026-06-14 with the auth-redirect fix.)
};
